/**
 * 云牛集 · 牛只交易平台 — 字典常量与业务规则常量
 * 对齐架构设计 §8.2 / UI 规格 §3（IR-14 费率规则）
 */
import type { SyntheticEvent } from 'react';

// ===== COS 静态资源（对象存储）=====
// 静态图片统一走腾讯云 COS；本地 public/images 保留作为 COS 不可达时的回退。
export const COS_BASE = 'https://aiotagro-1326635641.cos.ap-nanjing.myqcloud.com/shangdong';

/** COS 图片目录前缀（与上传对象键 shangdong/images/ 对齐） */
export const IMAGE_BASE = `${COS_BASE}/images`;

/**
 * 将本地图片路径（如 /images/cattle-default.jpg）转换为 COS 公网链接。
 * 已是绝对 URL 的（http/https/data:/blob:）原样返回，便于后端返回 COS 地址时透传。
 */
export function cosUrl(path: string): string {
  if (!path) return path;
  if (/^(https?:|data:|blob:)/i.test(path)) return path;
  const name = path.replace(/^\/+/, '').replace(/^images\//, '');
  return `${IMAGE_BASE}/${name}`;
}

/**
 * <img> 加载失败时回退到本地图。
 * 用法：<img src={cosUrl('/images/x.jpg')} data-local="/images/x.jpg" onError={imgFallback} />
 * 本地图也失败时停止重试，避免死循环。
 */
export function imgFallback(e: SyntheticEvent<HTMLImageElement>): void {
  const el = e.currentTarget;
  const local = el.dataset.local;
  if (local && el.src !== local) {
    el.src = local;
    el.onerror = null;
  }
}

// ===== 费率与交易规则（IR-14）=====
export const SERVICE_FEE_RATE_FIXED = 0.015; // 一口价 1.5%
export const SERVICE_FEE_RATE_AUCTION = 0.02; // 竞价 2.0%
export const SERVICE_FEE_MIN = 50; // 最低服务费 50 元/单
export const BULK_THRESHOLD = 50; // 大宗议价 ≥50 头
export const FREE_FEE_USER_TYPES: number[] = [4]; // 散户免佣（user_type=4）
export const PRICE_MIN = 1000; // 挂牌价格下限（元/头）
export const PRICE_MAX = 500000; // 挂牌价格上限（元/头）
export const LOCK_MINUTES = 15; // 一口价下单锁定 15 分钟（IR-05/06）
export const AUCTION_EXTEND_MINUTES = 2; // 竞价防秒杀延长 2 分钟（IR-02）
export const ARBITRATION_DAYS = 7; // 仲裁 7 个自然日（IR-09）
export const REVIEW_DAYS = 15; // 评价 15 天（IR-15）
export const PAGE_SIZE_DEFAULT = 20; // 分页默认每页 20

// ===== 订单状态机（8 态，对齐后端 model/trade.go 代码枚举 P0-5 2026-08-20）=====
// ★ 口径说明（审查报告 N10/Q5）：以代码为准，勿引入 PRD 语义 —— 代码中
//   0-待付款 1-已付款 2-已发货 3-已完成 4-已取消 5-已退款 6-已过期 7-售后中；
//   「已结算」不是独立枚举值，而是 Completed(3) + settled_at 非空的派生标记
//   （后端 model.TradeOrder.IsOrderSettled()/SettledAt）。
export interface StatusMeta {
  label: string;
  tone: 'default' | 'success' | 'danger' | 'info' | 'warning' | 'gold' | 'teal';
}

export const ORDER_STATUS: Record<number, StatusMeta> = {
  0: { label: '待付款', tone: 'warning' },
  1: { label: '已付款', tone: 'info' },
  2: { label: '已发货', tone: 'info' },
  3: { label: '已完成', tone: 'success' },
  4: { label: '已取消', tone: 'danger' },
  5: { label: '已退款', tone: 'danger' },
  6: { label: '已过期', tone: 'danger' },
  7: { label: '售后中', tone: 'gold' },
};

export const ORDER_STATUS_LIST = [0, 1, 2, 3, 4, 5, 6, 7];

export const ORDER_PROGRESS = ['待付款', '已付款', '已发货', '已完成'];

/** 已结算派生标记：order_status=3(已完成) 且 settled_at 非空（对齐后端 IsOrderSettled） */
export function isOrderSettled(o: { order_status?: number; settled_at?: string | null } | null | undefined): boolean {
  return !!o && o.order_status === 3 && !!o.settled_at;
}

/** 订单展示元信息：已结算(3+settled_at) 优先展示「已结算」，其余按代码枚举映射 */
export function orderStatusMeta(status: number, settled?: boolean): StatusMeta {
  if (settled && status === 3) return { label: '已结算', tone: 'gold' };
  return ORDER_STATUS[status] ?? { label: '未知', tone: 'default' };
}

// 订单状态 → 进度条节点下标（正常流转）；异常态返回 -1
export function orderProgressIndex(status: number): number {
  const map: Record<number, number> = { 0: 0, 1: 1, 2: 2, 3: 3 };
  return map[status] ?? -1;
}

// ===== 经纪委托状态机（5 态）=====
export const BROKER_ORDER_STATUS: Record<number, StatusMeta> = {
  0: { label: '待接单', tone: 'info' },
  1: { label: '撮合中', tone: 'gold' },
  2: { label: '已成交', tone: 'success' },
  3: { label: '已取消', tone: 'danger' },
  4: { label: '已超时', tone: 'danger' },
};

// ===== 竞价状态 =====
export const AUCTION_STATUS: Record<number, StatusMeta> = {
  0: { label: '预告', tone: 'info' },
  1: { label: '竞价中', tone: 'gold' },
  2: { label: '已成交', tone: 'success' },
  3: { label: '流拍', tone: 'danger' },
  4: { label: '已取消', tone: 'danger' },
};

// ===== 牛只状态 =====
export const CATTLE_STATUS: Record<number, StatusMeta> = {
  0: { label: '在栏', tone: 'success' },
  1: { label: '已售', tone: 'default' },
  2: { label: '死亡', tone: 'danger' },
  3: { label: '屠宰', tone: 'danger' },
};

// ===== 挂牌状态（Listing.status）=====
export const LISTING_STATUS: Record<number, StatusMeta> = {
  0: { label: '待审核', tone: 'warning' },
  1: { label: '在售', tone: 'success' },
  2: { label: '已下架', tone: 'default' },
  3: { label: '已售', tone: 'default' },
  4: { label: '已驳回', tone: 'danger' },
};

// ===== 挂牌状态 =====
export const SALE_STATUS: Record<number, StatusMeta> = {
  0: { label: '在售', tone: 'success' },
  1: { label: '已锁定', tone: 'warning' },
  2: { label: '已售出', tone: 'default' },
  3: { label: '已下架', tone: 'default' },
  4: { label: '待审核', tone: 'warning' },
};

// ===== 交易方式 =====
export const TRADE_MODE: Record<number, StatusMeta> = {
  1: { label: '一口价', tone: 'teal' },
  2: { label: '竞价', tone: 'gold' },
  3: { label: '可议价', tone: 'default' },
};

// ===== 订单类型（对齐后端：1现货/2预售/3拍卖/4经纪撮合，评审 D2）=====
export const ORDER_TYPE: Record<number, string> = {
  1: '现货',
  2: '预售',
  3: '拍卖',
  4: '经纪撮合',
};

// ===== 支付方式（对齐后端 model.TradeOrder：1线下汇款/2线上支付/3信用支付）=====
export const PAYMENT_METHOD: Record<number, string> = {
  1: '线下汇款',
  2: '线上支付',
  3: '信用支付',
};

// ===== 交割方式 =====
export const DELIVERY_METHOD: Record<number, string> = {
  1: '现场验收',
  2: '远程视频验收',
};

// ===== 用户类型 =====
export const USER_TYPE: Record<number, string> = {
  1: '管理员',
  2: '园区',
  3: '企业',
  4: '散户',
};

// ===== 在途状态 =====
export const TRANSIT_STATUS: Record<number, StatusMeta> = {
  0: { label: '待装车', tone: 'info' },
  1: { label: '运输中', tone: 'gold' },
  2: { label: '已到达', tone: 'info' },
  3: { label: '已签收', tone: 'success' },
};

// ===== 牛只字典 =====
export const BREEDS = ['西门塔尔', '安格斯', '夏洛莱', '利木赞', '本地黄牛', '娟姗'];

export const GENDER_MAP: Record<number, string> = { 0: '母', 1: '公' };

export const REGIONS = [
  '济南', '青岛', '淄博', '枣庄', '东营', '烟台', '潍坊',
  '济宁', '泰安', '威海', '日照', '临沂', '德州', '聊城', '滨州', '菏泽',
];

export const CATEGORIES: { key: string; name: string; desc: string }[] = [
  { key: '育肥牛', name: '育肥牛', desc: '出栏在即 · 体重达标 · 适合快速补栏' },
  { key: '种牛', name: '种牛', desc: '优质种源 · 血统清晰 · 繁育保障' },
  { key: '母牛', name: '母牛', desc: '能繁母牛 · 产犊稳定 · 效益可期' },
  { key: '犊牛', name: '犊牛', desc: '健康犊牛 · 检疫合格 · 育成空间大' },
];

export const QUICK_BIDS = [100, 200, 500]; // 竞价快捷加价（对齐设计文档 §3.1 与后端 validAuctionIncrement）
